// ElementPalette.jsx — Five-tab icon palette for MIL-STD symbol browsing.
// Ticket 04: real MSLookup-driven catalogue with SVG thumbnails.

import React, {useState, useEffect, useMemo, useCallback, useRef, useDeferredValue} from 'react';
import PropTypes from 'prop-types';
import {
    Box, Stack, Typography, TextField, InputAdornment,
    Tabs, Tab,
    Accordion, AccordionSummary, AccordionDetails,
    Chip, CircularProgress,
} from '@mui/material';
import SearchRounded from '@mui/icons-material/SearchRounded';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import PrecisionManufacturingRoundedIcon from '@mui/icons-material/PrecisionManufacturingRounded';
import PolylineRoundedIcon from '@mui/icons-material/PolylineRounded';
import CategoryRoundedIcon from '@mui/icons-material/CategoryRounded';
import CloudRoundedIcon from '@mui/icons-material/CloudRounded';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {MSLookup, SymbolID, isReady as milSymTsIsReady} from '@armyc2.c5isr.renderer/mil-sym-ts-web';
import {useRendererSettings} from '../map/editor/milsymbolSettings';
import {renderToDataURL, ensureInit} from '../map/editor/renderer/SymbolRenderer';
import {resolveDrawMode} from '../map/editor/resolveDrawMode';

// ── Constants ─────────────────────────────────────────────────────────────────

const PREVIEW_SIZE = 28;

// Tab index constants (mirrors TABS array order)
const TAB_FORMATIONS   = 0;
const TAB_EQUIPMENT    = 1;
const TAB_TACTICAL     = 2;
const TAB_FUNCTION     = 3;
const TAB_METOC        = 4;

// Symbol sets per tab
const FORMATIONS_SETS = new Set([
    SymbolID.SymbolSet_LandUnit,
    SymbolID.SymbolSet_LandCivilianUnit_Organization,
    SymbolID.SymbolSet_DismountedIndividuals,
]);

const EQUIPMENT_SETS = new Set([
    SymbolID.SymbolSet_LandEquipment,
    SymbolID.SymbolSet_LandInstallation,
    SymbolID.SymbolSet_Air,
    SymbolID.SymbolSet_AirMissile,
    SymbolID.SymbolSet_Space,
    SymbolID.SymbolSet_SpaceMissile,
    SymbolID.SymbolSet_SeaSurface,
    SymbolID.SymbolSet_SeaSubsurface,
    SymbolID.SymbolSet_MineWarfare,
]);

const FUNCTION_SETS = new Set([
    SymbolID.SymbolSet_Activities,
    SymbolID.SymbolSet_ControlMeasure,
    SymbolID.SymbolSet_SignalsIntelligence_Space,
    SymbolID.SymbolSet_SignalsIntelligence_Air,
    SymbolID.SymbolSet_SignalsIntelligence_Land,
    SymbolID.SymbolSet_SignalsIntelligence_SeaSurface,
    SymbolID.SymbolSet_SignalsIntelligence_SeaSubsurface,
    SymbolID.SymbolSet_CyberSpace,
    SymbolID.SymbolSet_CyberSpace_Equipment,
]);

const METOC_SETS = new Set([
    SymbolID.SymbolSet_Atmospheric,
    SymbolID.SymbolSet_Oceanographic,
    SymbolID.SymbolSet_MeteorologicalSpace,
]);

// Symbol-set number → human-readable label, used as the sub-group fallback for
// entries with no 2nd MSLookup path segment (see subGroupFromPath below).
// Mirrors the SS_LABEL maps in SymbolGallery.jsx / TacticalPalette.jsx.
const SS_LABEL = {
    [SymbolID.SymbolSet_LandUnit]:                          'Land Unit',
    [SymbolID.SymbolSet_LandCivilianUnit_Organization]:     'Civilian',
    [SymbolID.SymbolSet_DismountedIndividuals]:             'Dismounted',
    [SymbolID.SymbolSet_LandEquipment]:                     'Land Equipment',
    [SymbolID.SymbolSet_LandInstallation]:                  'Land Installation',
    [SymbolID.SymbolSet_Air]:                               'Air',
    [SymbolID.SymbolSet_AirMissile]:                        'Air Missile',
    [SymbolID.SymbolSet_Space]:                             'Space',
    [SymbolID.SymbolSet_SpaceMissile]:                      'Space Missile',
    [SymbolID.SymbolSet_SeaSurface]:                        'Sea Surface',
    [SymbolID.SymbolSet_SeaSubsurface]:                     'Sea Subsurface',
    [SymbolID.SymbolSet_MineWarfare]:                       'Mine Warfare',
    [SymbolID.SymbolSet_Activities]:                        'Activities',
    [SymbolID.SymbolSet_ControlMeasure]:                    'Control Measure',
    [SymbolID.SymbolSet_Atmospheric]:                       'Atmospheric',
    [SymbolID.SymbolSet_Oceanographic]:                     'Oceanographic',
    [SymbolID.SymbolSet_MeteorologicalSpace]:               'Meteorological Space',
    [SymbolID.SymbolSet_SignalsIntelligence_Space]:         'Space SIGINT',
    [SymbolID.SymbolSet_SignalsIntelligence_Air]:           'Air SIGINT',
    [SymbolID.SymbolSet_SignalsIntelligence_Land]:          'Land SIGINT',
    [SymbolID.SymbolSet_SignalsIntelligence_SeaSurface]:    'Sea Surface SIGINT',
    [SymbolID.SymbolSet_SignalsIntelligence_SeaSubsurface]: 'Sea Subsurface SIGINT',
    [SymbolID.SymbolSet_CyberSpace]:                        'Cyberspace',
    [SymbolID.SymbolSet_CyberSpace_Equipment]:              'Cyberspace Equipment',
};

// Geometry-type badge labels/colours for Tactical Graphics
const MODE_LABEL = {
    'draw-line':    'Line',
    'draw-polygon': 'Area',
    'draw-point':   'Point',
    'draw-circle':  'Circle',
};
const MODE_COLOR = {
    'draw-line':    'info',
    'draw-polygon': 'success',
    'draw-point':   'default',
    'draw-circle':  'warning',
};

// ── Tab definitions ──────────────────────────────────────────────────────────

const TABS = [
    {label: 'Formations',              icon: <GroupsRoundedIcon sx={{fontSize: 20}}/>},
    {label: 'Equipment & Installs',    icon: <PrecisionManufacturingRoundedIcon sx={{fontSize: 20}}/>},
    {label: 'Tactical Graphics',       icon: <PolylineRoundedIcon sx={{fontSize: 20}}/>},
    {label: 'Function-Specific',       icon: <CategoryRoundedIcon sx={{fontSize: 20}}/>},
    {label: 'Metoc',                   icon: <CloudRoundedIcon sx={{fontSize: 20}}/>},
];

// ── Catalogue builder ────────────────────────────────────────────────────────

// Build a SIDC from the MSLookup basicID (SymbolSet+EntityCode, 8 chars).
// positions: 11|0|3|SS|0|0|00|EE|ET|ES|00|00 = 20 chars
function basicIdToSIDC(basicID) {
    const ss        = basicID.slice(0, 2);
    const ec        = basicID.slice(2);
    const entity    = ec.slice(0, 2) || '00';
    const entityTyp = ec.slice(2, 4) || '00';
    const entitySub = ec.slice(4, 6) || '00';
    return `1103${ss}0000${entity}${entityTyp}${entitySub}0000`;
}

// Derive the sub-group label from the 2nd MSLookup path segment (the real DOD
// doctrinal sub-category, e.g. "Movement and Maneuver" for a Land Unit symbol).
// The 1st segment is just the symbol-set name — already implied by the tab, so
// it's redundant as a grouping key. Entries with no 2nd segment (root-level
// entries) fall back to a group named after their own symbol-set.
export function subGroupFromPath(path, symbolSet) {
    const fallback = SS_LABEL[symbolSet] || `Set ${symbolSet}`;
    if (!path) return fallback;
    const segments = path.split('/').map((s) => s.trim()).filter(Boolean);
    return segments[1] || fallback;
}

// Build the master catalogue once MSLookup is ready.
// Each entry:
//   { sidc, name, subGroup, tabIndex, mode? }
//   - tabIndex ∈ [0..4]
//   - mode is set only for Tactical Graphics entries (for badge display)
function buildMasterCatalogue(version) {
    const lookup = MSLookup.getInstance();
    if (!lookup.isReady()) return [];

    const ids   = lookup.getIDList(version);
    const seen  = new Set();
    const entries = [];

    for (const id of ids) {
        let info;
        try { info = lookup.getMSLInfo(id, version); } catch (_) { continue; }
        if (!info) continue;

        const ss   = info.getSymbolSet();
        const geom = info.getGeometry();
        const sidc = basicIdToSIDC(id);
        if (seen.has(sidc)) continue;

        const name     = info.getName() || id;
        const subGroup = subGroupFromPath(info.getPath() || '', ss);

        // ── Tactical Graphics tab: all non-point symbols ──────────────────
        if (geom !== 'point') {
            let mode;
            try {
                const resolved = resolveDrawMode(info);
                mode = resolved?.mode ?? null;
            } catch (_) { mode = null; }
            if (!mode) continue; // DONOTDRAW — category node
            seen.add(sidc);
            entries.push({sidc, name, subGroup, tabIndex: TAB_TACTICAL, mode});
            continue;
        }

        // ── Point symbols ─────────────────────────────────────────────────
        let tabIndex = null;

        if (FORMATIONS_SETS.has(ss)) {
            tabIndex = TAB_FORMATIONS;
        } else if (EQUIPMENT_SETS.has(ss)) {
            tabIndex = TAB_EQUIPMENT;
        } else if (METOC_SETS.has(ss)) {
            tabIndex = TAB_METOC;
        } else if (FUNCTION_SETS.has(ss)) {
            // ControlMeasure with point geometry → Function-Specific; otherwise skip (multipoint already handled above)
            tabIndex = TAB_FUNCTION;
        }

        if (tabIndex === null) continue;

        seen.add(sidc);
        entries.push({sidc, name, subGroup, tabIndex});
    }
    return entries;
}

// ── SymbolThumb — deferred / staggered SVG thumbnail rendering ───────────────
//
// renderToDataURL is expensive enough that rendering dozens/hundreds of rows
// synchronously on first accordion expand causes visible jank. Instead each
// SymbolThumb schedules its own render via requestIdleCallback (falling back
// to setTimeout where unavailable, e.g. jsdom), and caches the result in a
// module-level Map so re-expanding a group or revisiting a tab is instant.

// Module-level cache: sidc+size+settings → data URL. Survives unmount/remount
// so switching tabs and back doesn't redo the work.
const _thumbCache = new Map();

function thumbCacheKey(sidc, size, settings) {
    return `${sidc}|${size}|${JSON.stringify(settings || {})}`;
}

// Bound how long a render can be deferred. requestIdleCallback with no
// timeout is only a *hint* — the browser may never report an idle period
// while the main thread stays busy, which is exactly what happens during a
// burst of accordion toggling (each toggle forces a synchronous Collapse
// reflow). Passing `timeout` guarantees the callback still fires within this
// window regardless of load, so rapid toggling can't starve it forever.
const IDLE_TIMEOUT_MS = 300;

// requestIdleCallback isn't available in all browsers/environments (e.g. jsdom
// in tests) — fall back to a short setTimeout so scheduling still staggers.
const scheduleIdle = typeof window !== 'undefined' && typeof window.requestIdleCallback === 'function'
    ? (cb) => window.requestIdleCallback(cb, {timeout: IDLE_TIMEOUT_MS})
    : (cb) => setTimeout(() => cb({didTimeout: false, timeRemaining: () => 0}), 0);

const cancelIdle = typeof window !== 'undefined' && typeof window.cancelIdleCallback === 'function'
    ? (id) => window.cancelIdleCallback(id)
    : (id) => clearTimeout(id);

function computeThumb(sidc, size, settings) {
    try { return renderToDataURL(sidc, {}, {...settings, size}); }
    catch (_) { return null; }
}

// immediate: skip idle/rAF staggering and compute synchronously (used for
// search results, which must render without delay).
//
// ghostRef: when provided, a SECOND <img> is rendered (sharing the same
// dataUrl, so no extra render/cache work) that setDragImage should target
// instead of the visible list thumbnail. Chrome requires the setDragImage
// source element to be genuinely rendered/visible in the viewport —
// display:none, visibility:hidden, opacity:0, and off-screen positioning
// (with or without a transform) are all well-documented to make it silently
// fail there (see MDN's own note on setDragImage, and kryogenix.org's
// "Setting a custom ghost image" writeup). The only reliable cross-browser
// technique is to keep the ghost at a REAL on-screen position but cover it
// with an opaque sibling at that same position: the ghost is absolutely
// positioned at the top-left corner of a small position:relative wrapper,
// with a lower z-index and pointerEvents:none, directly UNDERNEATH the
// visible thumbnail <img> that occupies that same wrapper (and therefore
// the same on-screen coordinates). The browser genuinely paints the ghost
// (satisfying the "must be visible" requirement) but it's always fully
// covered by the opaque visible thumbnail sitting on top of it, so the user
// never sees it and it never affects the palette's layout or click/drag
// handling on the visible icon. Crucially the ghost has NO width/height
// attributes, so the browser sizes it from the SVG's own intrinsic
// (possibly non-square) root width/height instead of forcing it into the
// fixed PREVIEW_SIZE square box. That square box is what caused the white
// letterboxing artifact above/below wide symbols: Chrome's drag-ghost
// rasterization paints an <img>'s entire box onto an opaque backing
// surface, so the transparent margin the SVG's default `xMidYMid meet`
// scaling leaves inside a mismatched-aspect-ratio square box rendered as
// solid white specifically in the drag ghost (not in normal DOM paint,
// which stays transparent). The visible thumbnail keeps its fixed square
// sizing unchanged — that's intentional for consistent row layout.
// While still loading (CircularProgress shown), no ghost <img> is rendered
// at all — callers should treat a null ghostRef.current as "skip the custom
// drag image for this drag" rather than pointing it at the spinner.
function SymbolThumb({sidc, size, settings, immediate, ghostRef}) {
    const key = thumbCacheKey(sidc, size, settings);
    const [dataUrl, setDataUrl] = useState(() => _thumbCache.get(key) ?? null);

    useEffect(() => {
        // A cache HIT only ever reflects a genuine successful render (see
        // `commit` below) — a transient failure is never cached, so a miss
        // here always means "not yet rendered", never "previously failed,
        // give up forever".
        const cached = _thumbCache.get(key);
        if (cached) {
            setDataUrl(cached);
            return;
        }
        const commit = (result) => {
            if (result) _thumbCache.set(key, result);
            setDataUrl(result || null);
        };
        if (immediate) {
            commit(computeThumb(sidc, size, settings));
            return;
        }
        let cancelled = false;
        const handle = scheduleIdle(() => {
            if (!cancelled) commit(computeThumb(sidc, size, settings));
        });
        return () => { cancelled = true; cancelIdle(handle); };
    }, [key, sidc, size, settings, immediate]);

    if (!dataUrl) return (
        <Box sx={{width: size, height: size, display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
            <CircularProgress size={size * 0.6} thickness={5}/>
        </Box>
    );
    return (
        <Box sx={{position: 'relative', width: size, height: size, flexShrink: 0, overflow: 'hidden'}}>
            {ghostRef && (
                <img
                    ref={ghostRef}
                    src={dataUrl}
                    alt=""
                    aria-hidden
                    style={{
                        display: 'block',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        zIndex: 0,
                        pointerEvents: 'none',
                    }}
                />
            )}
            {/* Opaque backdrop between the ghost and the visible thumbnail below.
                The ghost above is deliberately left unsized (see ghostRef comment),
                so it renders at its native, uncropped resolution — usually larger
                than this size×size box — and gets cropped by overflow:hidden to
                just its top-left corner. Left uncovered, that cropped corner shows
                through any transparent letterbox padding the visible thumbnail
                leaves for non-square symbols, appearing as a second, smaller,
                misaligned symbol peeking out from behind it. This backdrop blocks
                exactly that sliver without affecting the ghost's own rasterized
                drag image (which is captured straight from the ghost element,
                ignoring sibling paint order/z-index). */}
            {ghostRef && (
                <Box sx={{
                    position: 'absolute', top: 0, left: 0, width: size, height: size,
                    zIndex: 1, bgcolor: 'background.paper',
                }}/>
            )}
            <img
                src={dataUrl}
                width={size}
                height={size}
                alt=""
                aria-hidden
                style={{display: 'block', position: 'relative', zIndex: 2}}
            />
        </Box>
    );
}

SymbolThumb.propTypes = {
    sidc: PropTypes.string.isRequired,
    size: PropTypes.number.isRequired,
    settings: PropTypes.object,
    immediate: PropTypes.bool,
    ghostRef: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
};

// ── Shared sx constants ──────────────────────────────────────────────────────

const TAB_BAR_SX = {
    flexShrink: 0,
    minHeight: 60,
    borderBottom: 1,
    borderColor: 'divider',
    '& .MuiTabs-indicator': {height: 2},
    '& .MuiTab-root': {
        minHeight: 60,
        fontSize: 9,
        fontWeight: 600,
        textTransform: 'none',
        px: 0.75,
        py: 0.5,
        minWidth: 0,
        lineHeight: 1.2,
    },
};

const ACCORDION_SX = {
    '&:before': {display: 'none'},
    borderBottom: 1,
    borderColor: 'divider',
};

const ACCORDION_SUMMARY_SX = {
    px: 1.5,
    minHeight: 32,
    '&.Mui-expanded': {minHeight: 32},
    '& .MuiAccordionSummary-content': {my: 0.5},
    '& .MuiAccordionSummary-expandIconWrapper': {
        '& svg': {fontSize: 14},
    },
};

const ACCORDION_DETAILS_SX = {
    px: 1.5,
    pt: 0.5,
    pb: 1,
};

// ── SearchBar ────────────────────────────────────────────────────────────────

function SearchBar({value, onChange, placeholder = 'Search symbols\u2026'}) {
    return (
        <Box sx={{px: 1, pt: 1, pb: 0.5, flexShrink: 0}}>
            <TextField
                size="small"
                fullWidth
                placeholder={placeholder}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start">
                            <SearchRounded sx={{fontSize: 16, color: 'text.disabled'}}/>
                        </InputAdornment>
                    ),
                }}
                inputProps={{style: {fontSize: 12, padding: '4px 6px'}}}
            />
        </Box>
    );
}

SearchBar.propTypes = {
    value: PropTypes.string.isRequired,
    onChange: PropTypes.func.isRequired,
    placeholder: PropTypes.string,
};

// ── SymbolEntry — single clickable row inside an accordion ───────────────────

const SymbolEntry = React.memo(function SymbolEntry({entry, isTactical, isSelected, settings, onSelectSidc, onSelectTacticalSidc, immediate}) {
    // Clicking the already-pending entry toggles it off (deselects), matching
    // the toggle behavior in SymbolGallery.jsx's handleSelect.
    const handleClick = useCallback(() => {
        const next = isSelected ? null : entry.sidc;
        if (isTactical) {
            onSelectTacticalSidc?.(next);
        } else {
            onSelectSidc?.(next);
        }
    }, [entry.sidc, isTactical, isSelected, onSelectSidc, onSelectTacticalSidc]);

    // Stable node to point setDragImage at, so the browser's drag ghost is
    // just the icon rather than a snapshot of the whole row (icon+name+chip).
    // This is a dedicated, hidden ghost <img> (see SymbolThumb's ghostRef
    // comment) — deliberately NOT the visible list thumbnail, since that one
    // is forced into a fixed square box that doesn't match most symbols'
    // natural (often non-square) aspect ratio.
    const ghostRef = useRef(null);

    return (
        <Box
            role="option"
            aria-selected={isSelected}
            onClick={handleClick}
            draggable={!isTactical}
            onDragStart={(e) => {
                if (isTactical) return;
                e.dataTransfer.setData('text/x-sidc', entry.sidc);
                const ghost = ghostRef.current;
                if (ghost && e.dataTransfer.setDragImage) {
                    // Center the drag ghost on the cursor using its OWN rendered
                    // size — it's no longer a fixed PREVIEW_SIZE square, so the
                    // offset must reflect the actual (aspect-correct) box.
                    const w = ghost.offsetWidth || PREVIEW_SIZE;
                    const h = ghost.offsetHeight || PREVIEW_SIZE;
                    e.dataTransfer.setDragImage(ghost, w / 2, h / 2);
                }
            }}
            sx={(theme) => ({
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
                px: 0.75,
                py: 0.4,
                borderRadius: 1,
                cursor: 'pointer',
                border: isSelected
                    ? `1.5px solid ${theme.palette.primary.main}`
                    : `1.5px solid ${theme.palette.divider}`,
                bgcolor: isSelected ? 'primary.50' : 'transparent',
                '&:hover': {bgcolor: isSelected ? 'primary.100' : 'action.hover'},
                transition: 'background-color 0.1s',
                mb: 0.4,
            })}
        >
            <SymbolThumb sidc={entry.sidc} size={PREVIEW_SIZE} settings={settings} immediate={immediate} ghostRef={ghostRef}/>
            <Typography sx={{flex: 1, minWidth: 0, fontSize: 11, lineHeight: 1.3, overflow: 'hidden',
                fontWeight: isSelected ? 600 : 400,
                display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', wordBreak: 'break-word'}}>
                {entry.name}
            </Typography>
            {isTactical && entry.mode && (
                <Chip
                    label={MODE_LABEL[entry.mode] ?? entry.mode}
                    color={MODE_COLOR[entry.mode] ?? 'default'}
                    size="small"
                    sx={{height: 16, fontSize: 9, flexShrink: 0, '& .MuiChip-label': {px: 0.5}}}
                />
            )}
        </Box>
    );
});

SymbolEntry.propTypes = {
    entry: PropTypes.shape({
        sidc: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired,
        mode: PropTypes.string,
    }).isRequired,
    isTactical: PropTypes.bool,
    isSelected: PropTypes.bool,
    settings: PropTypes.object,
    onSelectSidc: PropTypes.func,
    onSelectTacticalSidc: PropTypes.func,
    immediate: PropTypes.bool,
};

// ── GroupAccordion — accordion with real entries ──────────────────────────────
//
// AccordionDetails (the row list + thumbnails) is deferred until first expand
// via TransitionProps.mountOnEnter — MUI's Accordion mounts its children
// eagerly by default (verified: AccordionDetails content renders even while
// collapsed), so without this the "don't render until first expand" behavior
// from ticket 02 would not hold. unmountOnExit stays false so collapsing
// again doesn't tear down the already-rendered rows/thumbnails. timeout: 0
// disables the slide animation (ticket 03) so expand/collapse is instant.
const GROUP_TRANSITION_PROPS = {mountOnEnter: true, unmountOnExit: false, timeout: 0};

function GroupAccordion({label, entries, isTactical, pendingSidc, pendingTacticalSidc, settings, onSelectSidc, onSelectTacticalSidc, expanded, onToggle, immediate}) {
    return (
        <Accordion
            disableGutters elevation={0} sx={ACCORDION_SX}
            expanded={expanded}
            onChange={(_, v) => onToggle(v)}
            TransitionProps={GROUP_TRANSITION_PROPS}
        >
            <AccordionSummary expandIcon={<ExpandMoreIcon/>} sx={ACCORDION_SUMMARY_SX}>
                <Typography sx={{fontSize: 11, fontWeight: 600}}>
                    {label}
                </Typography>
            </AccordionSummary>
            <AccordionDetails sx={ACCORDION_DETAILS_SX} role="listbox">
                {entries.map((entry) => (
                    <SymbolEntry
                        key={entry.sidc}
                        entry={entry}
                        isTactical={isTactical}
                        isSelected={isTactical ? pendingTacticalSidc === entry.sidc : pendingSidc === entry.sidc}
                        settings={settings}
                        onSelectSidc={onSelectSidc}
                        onSelectTacticalSidc={onSelectTacticalSidc}
                        immediate={immediate}
                    />
                ))}
            </AccordionDetails>
        </Accordion>
    );
}

GroupAccordion.propTypes = {
    label: PropTypes.string.isRequired,
    entries: PropTypes.array.isRequired,
    isTactical: PropTypes.bool,
    pendingSidc: PropTypes.string,
    pendingTacticalSidc: PropTypes.string,
    settings: PropTypes.object,
    onSelectSidc: PropTypes.func,
    onSelectTacticalSidc: PropTypes.func,
    expanded: PropTypes.bool.isRequired,
    onToggle: PropTypes.func.isRequired,
    immediate: PropTypes.bool,
};

// ── TabPanel — search + scrollable list of group accordions ──────────────────

function TabPanel({tabEntries, tabIndex, search, onSearchChange, pendingSidc, pendingTacticalSidc, settings, onSelectSidc, onSelectTacticalSidc}) {
    const isTactical = tabIndex === TAB_TACTICAL;
    // ticket 06: `search` itself stays live for the TextField below (so every keystroke is
    // reflected immediately) — only the value consumed by the subGroups/filteredGroups memos
    // (which re-filter/re-sort the full active-tab catalogue, expensive for Equipment/Tactical's
    // hundreds of entries) is deferred, letting React keep input responsive and catch the
    // filtered list up a frame later rather than blocking on it synchronously.
    const deferredSearch = useDeferredValue(search);
    const q = deferredSearch.trim().toLowerCase();

    // Manual expand/collapse state, keyed by sub-group label. Search overrides
    // this (forces matching groups open) without mutating it, so clearing the
    // search reverts to whatever the user manually opened/closed.
    const [manualOpen, setManualOpen] = useState(() => new Map());
    const handleToggle = useCallback((label, value) => {
        setManualOpen((prev) => {
            const next = new Map(prev);
            next.set(label, value);
            return next;
        });
    }, []);

    // Build filtered sub-groups
    const subGroups = useMemo(() => {
        const entries = tabEntries ?? [];
        // Group entries
        const groups = new Map();
        for (const entry of entries) {
            if (!groups.has(entry.subGroup)) groups.set(entry.subGroup, []);
            groups.get(entry.subGroup).push(entry);
        }
        // Sort entries within each group alphabetically
        for (const arr of groups.values()) {
            arr.sort((a, b) => a.name.localeCompare(b.name));
        }
        // Sort group names alphabetically
        const sorted = [...groups.entries()].sort(([a], [b]) => a.localeCompare(b));
        return sorted; // [[label, entries[]], ...]
    }, [tabEntries]);

    const filteredGroups = useMemo(() => {
        if (!q) return subGroups;
        return subGroups
            .map(([label, entries]) => [
                label,
                entries.filter((e) => e.name.toLowerCase().includes(q) || e.sidc.includes(q)),
            ])
            .filter(([, entries]) => entries.length > 0);
    }, [q, subGroups]);

    const totalCount = filteredGroups.reduce((acc, [, arr]) => acc + arr.length, 0);

    return (
        <Stack sx={{height: '100%', overflow: 'hidden'}}>
            <SearchBar value={search} onChange={onSearchChange}/>
            <Box sx={{flex: 1, overflowY: 'auto'}}>
                {totalCount === 0 ? (
                    <Box sx={{px: 2, py: 3, textAlign: 'center'}}>
                        <Typography variant="caption" sx={{color: 'text.disabled'}}>
                            {q ? 'No results for your search.' : 'No symbols available.'}
                        </Typography>
                    </Box>
                ) : (
                    filteredGroups.map(([label, entries]) => (
                        <GroupAccordion
                            key={label}
                            label={label}
                            entries={entries}
                            isTactical={isTactical}
                            pendingSidc={pendingSidc}
                            pendingTacticalSidc={pendingTacticalSidc}
                            settings={settings}
                            onSelectSidc={onSelectSidc}
                            onSelectTacticalSidc={onSelectTacticalSidc}
                            // While searching, every rendered group already has matches
                            // (empty groups are filtered out above) — auto-expand and
                            // render its thumbnails immediately, skipping the stagger.
                            expanded={q ? true : (manualOpen.get(label) ?? false)}
                            onToggle={(value) => handleToggle(label, value)}
                            immediate={Boolean(q)}
                        />
                    ))
                )}
            </Box>
        </Stack>
    );
}

TabPanel.propTypes = {
    tabEntries: PropTypes.array.isRequired,
    tabIndex: PropTypes.number.isRequired,
    search: PropTypes.string.isRequired,
    onSearchChange: PropTypes.func.isRequired,
    pendingSidc: PropTypes.string,
    pendingTacticalSidc: PropTypes.string,
    settings: PropTypes.object,
    onSelectSidc: PropTypes.func,
    onSelectTacticalSidc: PropTypes.func,
};

// ── Main ElementPalette ───────────────────────────────────────────────────────

const ElementPalette = ({
    activeTabIndex,
    onTabChange,
    search,
    onSearchChange,
    pendingSidc,
    onSelectSidc,
    pendingTacticalSidc,
    onSelectTacticalSidc,
}) => {
    const {settings} = useRendererSettings();
    const [ready, setReady] = useState(milSymTsIsReady());

    useEffect(() => {
        if (ready) return;
        ensureInit().then(() => setReady(true)).catch(() => {});
    }, [ready]);

    // Build master catalogue once after MSLookup is ready
    const masterCatalogue = useMemo(() => {
        if (!ready) return [];
        return buildMasterCatalogue(SymbolID.Version_2525Dch1);
    }, [ready]);

    // Per-tab catalogue slices (derived from master, no extra build calls)
    const tabCatalogues = useMemo(() => {
        const tabs = [[], [], [], [], []];
        for (const entry of masterCatalogue) {
            tabs[entry.tabIndex].push(entry);
        }
        return tabs;
    }, [masterCatalogue]);

    const safeIndex = Math.min(Math.max(0, Number.isFinite(activeTabIndex) ? activeTabIndex : 0), TABS.length - 1);

    if (!ready) {
        return (
            <Stack sx={{height: '100%', overflow: 'hidden'}}>
                <Tabs
                    value={safeIndex}
                    onChange={(_, newValue) => onTabChange(newValue)}
                    variant="scrollable"
                    scrollButtons={false}
                    sx={TAB_BAR_SX}
                >
                    {TABS.map((tab) => (
                        <Tab
                            key={tab.label}
                            icon={tab.icon}
                            iconPosition="top"
                            label={tab.label}
                            sx={{fontSize: 9}}
                        />
                    ))}
                </Tabs>
                <Stack alignItems="center" justifyContent="center" sx={{flex: 1, gap: 1, py: 4}}>
                    <CircularProgress size={20}/>
                    <Typography variant="caption" sx={{color: 'text.disabled'}}>Loading symbols…</Typography>
                </Stack>
            </Stack>
        );
    }

    return (
        <Stack sx={{height: '100%', overflow: 'hidden'}}>
            {/* Top-level icon tabs */}
            <Tabs
                value={safeIndex}
                onChange={(_, newValue) => onTabChange(newValue)}
                variant="scrollable"
                scrollButtons={false}
                sx={TAB_BAR_SX}
            >
                {TABS.map((tab) => (
                    <Tab
                        key={tab.label}
                        icon={tab.icon}
                        iconPosition="top"
                        label={tab.label}
                        sx={{fontSize: 9}}
                    />
                ))}
            </Tabs>

            {/* Active tab panel */}
            <Box sx={{flex: 1, overflow: 'hidden'}}>
                <TabPanel
                    key={safeIndex}
                    tabEntries={tabCatalogues[safeIndex] ?? []}
                    tabIndex={safeIndex}
                    search={search}
                    onSearchChange={onSearchChange}
                    pendingSidc={pendingSidc}
                    pendingTacticalSidc={pendingTacticalSidc}
                    settings={settings}
                    onSelectSidc={onSelectSidc}
                    onSelectTacticalSidc={onSelectTacticalSidc}
                />
            </Box>
        </Stack>
    );
};

ElementPalette.propTypes = {
    activeTabIndex: PropTypes.number.isRequired,
    onTabChange: PropTypes.func.isRequired,
    search: PropTypes.string.isRequired,
    onSearchChange: PropTypes.func.isRequired,
    pendingSidc: PropTypes.string,
    onSelectSidc: PropTypes.func,
    pendingTacticalSidc: PropTypes.string,
    onSelectTacticalSidc: PropTypes.func,
};

export default ElementPalette;
